import { useEffect, useState, useCallback } from "react";
import {
  transactionsApi,
  accountsApi,
  fundingBankAccountsApi,
  beneficiariesApi,
  transfersApi,
  payoutProvidersApi,
  conversionsApi,
  scheduledTransfersApi,
  moneyRequestsApi,
  invoicesApi,
  billsApi,
  cardsApi,
  creditReportApi,
  referralApi,
  budgetsApi,
  investmentsApi,
  goalsApi,
  socialPaymentsApi,
  retirementPlansApi,
} from "../api/endpoints";

/** Adapt backend transaction shape to UI shape used across the app */
const adaptTransaction = (t) => ({
  id: t.reference || t.id,
  type: t.type,
  counterparty: t.counterparty,
  direction: t.direction,
  date: new Date(t.createdAt).toLocaleString("en-AU", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }),
  amount: Number(t.amount),
  currency: t.currency,
  destAmount: t.destAmount ? Number(t.destAmount) : undefined,
  destCurrency: t.destCurrency,
  status: t.status,
  category: t.category || "",
  reference: t.reference,
  raw: t,
});

/** Unified transaction history. Returns { items, loading, source, refresh }. */
export const useTransactions = ({ limit } = {}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("api");

  const refresh = useCallback(() => {
    setLoading(true);
    return transactionsApi
      .list()
      .then((data) => {
        const adapted = data.map(adaptTransaction);
        setItems(limit ? adapted.slice(0, limit) : adapted);
        setSource("api");
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [limit]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { items, loading, source, refresh };
};

/** Accounts (wallets) */
export const useAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState("api");

  const refresh = useCallback(() => {
    setLoading(true);
    return accountsApi
      .list()
      .then((data) => {
        setAccounts(data);
        setSource("api");
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { accounts, loading, error, source, refresh };
};

/** Linked external bank accounts used for direct debit funding. */
export const useFundingBankAccounts = () => {
  const [fundingBankAccounts, setFundingBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    return fundingBankAccountsApi
      .list()
      .then((data) => {
        setFundingBankAccounts(data);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { fundingBankAccounts, loading, error, refresh };
};

/** Beneficiaries */
export const useBeneficiaries = () => {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("api");

  const refresh = useCallback(() => {
    setLoading(true);
    return beneficiariesApi
      .list()
      .then((data) => {
        setBeneficiaries(data);
        setSource("api");
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { beneficiaries, loading, source, refresh };
};

/**
 * Sending limits and how much of the allowance is left.
 *
 * Without this the limits existed only as a rejection: a customer found out
 * about the 3,000 cap by being refused after they had already entered an
 * amount and requested a code.
 */
export const useTransferLimits = () => {
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return transfersApi
      .limits()
      .then((data) => {
        setLimits(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { limits, loading, refresh };
};

/**
 * Payout partners that serve a given currency.
 *
 * Driven by the destination currency because that is what decides who can
 * actually pay the money out, an AUD payout goes through Zepto, a GBP one
 * would go through iFAST Global. An empty list means ARMF has no partner for
 * that corridor yet, which the form says plainly instead of offering a method
 * that cannot work.
 */
export const usePayoutProviders = (currency) => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currency) {
      setProviders([]);
      return;
    }
    setLoading(true);
    payoutProvidersApi
      .forCurrency(currency)
      .then((data) => {
        setProviders(data?.providers ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [currency]);

  return { providers, loading };
};

/** All transfers, optionally filtered by status */
export const useTransfers = ({ status } = {}) => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("api");

  const refresh = useCallback(() => {
    setLoading(true);
    return transfersApi
      .list()
      .then((data) => {
        const filtered = status ? data.filter((t) => t.status === status) : data;
        setTransfers(filtered);
        setSource("api");
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, [status]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { transfers, loading, source, refresh };
};

/** Conversions */
export const useConversions = () => {
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return conversionsApi
      .list()
      .then((data) => {
        setConversions(data);
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { conversions, loading, refresh };
};

/** Scheduled Transfers */
export const useScheduledTransfers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("api");

  const refresh = useCallback(() => {
    setLoading(true);
    return scheduledTransfersApi.list()
      .then((data) => { setItems(data); setSource("api"); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { items, loading, source, refresh };
};

/** Money Requests */
export const useMoneyRequests = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return moneyRequestsApi.list()
      .then((data) => { setItems(data); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { items, loading, refresh };
};

/** Invoices */
export const useInvoices = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return invoicesApi.list()
      .then((data) => { setItems(data); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { items, loading, refresh };
};

/** Bills */
export const useBills = () => {
  const [bills, setBills] = useState([]);
  const [billers, setBillers] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return Promise.all([billsApi.listBills(), billsApi.listBillers()])
      .then(([billsData, billersData]) => {
        setBills(billsData);
        setBillers(billersData);
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { bills, billers, loading, refresh };
};

export const useCards = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return cardsApi
      .list()
      .then((data) => {
        setItems(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { items, loading, refresh };
};

export const useCreditReport = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return creditReportApi
      .get()
      .then((data) => {
        setProfile(data || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { profile, loading, refresh };
};

export const useReferralInvites = () => {
  const [summary, setSummary] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return Promise.all([referralApi.summary(), referralApi.listInvites()])
      .then(([summaryData, inviteData]) => {
        setSummary(summaryData || null);
        setInvites(Array.isArray(inviteData) ? inviteData : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { summary, invites, loading, refresh };
};

/** Budgets */
export const useBudgets = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return budgetsApi.list()
      .then((data) => { setItems(data); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { items, loading, refresh };
};

/** Social Payment Groups */
export const useGroups = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return socialPaymentsApi.listGroups()
      .then((data) => { setItems(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { items, loading, refresh };
};

export const useGroupExpenses = (groupId) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(groupId));

  const refresh = useCallback(() => {
    if (!groupId) {
      setItems([]);
      setLoading(false);
      return Promise.resolve([]);
    }
    setLoading(true);
    return socialPaymentsApi
      .listExpenses(groupId)
      .then((data) => {
        setItems(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [groupId]);

  useEffect(() => { refresh(); }, [refresh]);
  return { items, loading, refresh };
};

export const useAllGroupExpenses = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return socialPaymentsApi
      .listGroups()
      .then(async (groups) => {
        const groupList = Array.isArray(groups) ? groups : [];
        const nested = await Promise.all(
          groupList.map((group) =>
            socialPaymentsApi
              .listExpenses(group.id)
              .then((expenses) =>
                (Array.isArray(expenses) ? expenses : []).map((expense) => ({
                  ...expense,
                  group,
                }))
              )
              .catch(() => [])
          )
        );
        setItems(nested.flat());
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { items, loading, refresh };
};

export const useInvestments = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return investmentsApi
      .list()
      .then((data) => {
        setItems(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { items, loading, refresh };
};

export const useGoals = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return goalsApi
      .list()
      .then((data) => {
        setItems(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { items, loading, refresh };
};

/** Retirement plans + live projection. */
export const useRetirementPlans = () => {
  const [plans, setPlans] = useState([]);
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback((currentAge) => {
    setLoading(true);
    return Promise.all([
      retirementPlansApi.list(),
      retirementPlansApi.projection(currentAge),
    ])
      .then(([list, proj]) => {
        setPlans(list);
        setProjection(proj);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { plans, projection, loading, error, refresh };
};

/** Compute total equivalent balance in a chosen currency (uses 1:1 when FX unavailable) */
export const computeTotalBalance = (accounts, displayCurrency = "AUD") => {
  if (!accounts?.length) return { total: 0, byCurrency: {} };
  const byCurrency = {};
  for (const a of accounts) {
    byCurrency[a.currency] = (byCurrency[a.currency] || 0) + Number(a.balance);
  }
  const total = Object.entries(byCurrency).reduce((s, [, v]) => s + v, 0);
  return { total, byCurrency, displayCurrency };
};
